import { useState, useEffect, useCallback } from 'react';
import { requestNotificationPermission, onForegroundMessage, isConfigured } from '../services/firebase';
import api from '../services/api';
import { toast } from 'sonner';

const VAPID_KEY_STORAGE = 'fcm_vapid_key';
const TOKEN_STORAGE = 'fcm_token';

export const useNotifications = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [fcmToken, setFcmToken] = useState(null);
  const [loading, setLoading] = useState(false);

  // Register FCM token with backend
  const registerToken = useCallback(async (token) => {
    if (!token) return;
    try {
      await api.post('/push/register', {
        token,
        platform: 'web',
        user_agent: navigator.userAgent,
      });
      localStorage.setItem(TOKEN_STORAGE, token);
      setFcmToken(token);
    } catch (error) {
      console.error('Failed to register FCM token:', error);
    }
  }, []);

  // Request permission and get token
  const requestPermission = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch VAPID key from backend
      let vapidKey = localStorage.getItem(VAPID_KEY_STORAGE);
      if (!vapidKey) {
        try {
          const res = await api.get('/push/vapid-key');
          vapidKey = res.data.vapid_key;
          if (vapidKey) localStorage.setItem(VAPID_KEY_STORAGE, vapidKey);
        } catch {
          // VAPID key not configured yet
        }
      }

      const token = await requestNotificationPermission(vapidKey || undefined);
      if (token) {
        await registerToken(token);
        setPermission('granted');
        return true;
      }
      setPermission(Notification.permission);
      return false;
    } catch (error) {
      console.error('Notification permission error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [registerToken]);

  // Unregister token
  const unregister = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_STORAGE);
    if (token) {
      try {
        await api.post('/push/unregister', { token });
      } catch { /* ignore */ }
      localStorage.removeItem(TOKEN_STORAGE);
      setFcmToken(null);
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    onForegroundMessage((payload) => {
      const { title, body } = payload.notification || payload.data || {};
      if (title) {
        toast(title, { description: body });
      }
    });
  }, []);

  // Check existing token on mount
  useEffect(() => {
    const existing = localStorage.getItem(TOKEN_STORAGE);
    if (existing) setFcmToken(existing);
  }, []);

  return {
    permission,
    fcmToken,
    loading,
    requestPermission,
    unregister,
    isSupported: isConfigured && typeof Notification !== 'undefined' && 'serviceWorker' in navigator,
  };
};
